// Events — the raw feed, newest first: group chips, search (event type or
// runner), expandable rows, cursor pages and a CSV of up to 1,000 rows.
// Group and search live in the query string; ?type= (from the Overview
// health strip) becomes a search for that type.

import clsx from "clsx";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { EVENT_GROUPS, type AdminEventsPage, type EventGroup } from "@paceroo/domain/views/admin";
import { IconSearch } from "../../components/Icons";
import { ErrorState, LoadingState, Note, PageHeader } from "../components/ui";
import { adminGet, useAdminQuery } from "../lib/api";
import { eventSummary, isBadEvent } from "../lib/eventText";
import { fDT, n0, parseWhen } from "../lib/format";
import { useRange } from "../lib/useRange";
import { useShell } from "../shell/context";
import s from "./Events.module.css";

const GROUP_LABEL: Record<EventGroup, string> = { all: "All", auth: "Auth", plan: "Plans", export: "Exports", mcp: "MCP", api: "Errors" };
const PER_PAGE = { mobile: 20, tablet: 30, desktop: 30 } as const;
const CSV_LIMIT = 1000;

export function Events() {
  const range = useRange();
  const { bp, focusSearch } = useShell();
  const [params, setParams] = useSearchParams();
  const groupParam = params.get("group") ?? "all";
  const group: EventGroup = (EVENT_GROUPS as readonly string[]).includes(groupParam) ? (groupParam as EventGroup) : "all";
  const qParam = params.get("q") ?? "";
  const [q, setQ] = useState(qParam);
  // Page cursors, tied to the filters they were taken under.
  const [stack, setStack] = useState<{ key: string; cursors: Array<string | null> }>({ key: "", cursors: [null] });
  const [open, setOpen] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);
  const per = PER_PAGE[bp];

  const update = (changes: Record<string, string | null>) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        for (const [k, v] of Object.entries(changes)) {
          if (v === null || v === "" || (k === "group" && v === "all")) next.delete(k);
          else next.set(k, v);
        }
        return next;
      },
      { replace: true },
    );

  // ?type=auth.sign_in_failed → search for that type.
  useEffect(() => {
    const type = params.get("type");
    if (type) update({ type: null, q: type, group: null });
  });

  // The search box types freely; the URL follows 250 ms later.
  useEffect(() => setQ(qParam), [qParam]);
  useEffect(() => {
    if (q === qParam) return;
    const t = window.setTimeout(() => update({ q: q.trim() || null }), 250);
    return () => window.clearTimeout(t);
  });

  // New filters start from the newest page.
  const filterKey = `${range.days}|${group}|${qParam}|${per}`;
  const cursors = stack.key === filterKey ? stack.cursors : [null];
  const setCursors = (next: (c: Array<string | null>) => Array<string | null>) => setStack({ key: filterKey, cursors: next(cursors) });
  useEffect(() => setOpen(null), [filterKey]);

  const before = cursors[cursors.length - 1] ?? null;
  const query = new URLSearchParams({ days: String(range.days), limit: String(per) });
  if (group !== "all") query.set("group", group);
  if (qParam) query.set("q", qParam);
  const page = useAdminQuery<AdminEventsPage>(`events?${query.toString()}${before ? `&before=${encodeURIComponent(before)}` : ""}`);

  if (page.error && !page.data) return <ErrorState page="Events" error={page.error} onRetry={page.reload} onFind={focusSearch} />;
  if (!page.data) return <LoadingState page="events" />;

  const d = page.data;
  const mobile = bp === "mobile";
  const header = <PageHeader kicker={`Events · last ${d.range.days} days, raw`} before="Every event, " accent="in order." />;
  if (d.analytics === "unavailable" || !d.events || !d.groups) {
    return (
      <div className={s.page}>
        {header}
        <Note>The event feed needs Analytics Engine to be queryable in this environment.</Note>
      </div>
    );
  }

  const events = d.events;
  const groups = d.groups;
  const cols = mobile ? "1fr" : bp === "tablet" ? "96px 150px minmax(0,1fr) minmax(0,1.4fr)" : "108px 170px minmax(0,1fr) minmax(0,2fr)";
  const pageNo = cursors.length - 1;
  const from = pageNo * per + 1;

  const exportCsv = async () => {
    setExporting(true);
    try {
      const all = await adminGet<AdminEventsPage>(`events?${new URLSearchParams({ ...Object.fromEntries(query), limit: String(CSV_LIMIT) }).toString()}`);
      const rows = (all.events ?? []).map((e) => [e.at, e.event, e.runnerId ?? "", e.runner?.name ?? "", e.runner?.email ?? "", eventSummary(e), JSON.stringify(e.fields)]);
      const csv = [["at_utc", "event", "runner_id", "runner_name", "runner_email", "summary", "fields"], ...rows]
        .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `paceroo-events-${d.range.from}-to-${d.range.to}${group !== "all" ? `-${group}` : ""}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className={s.page}>
      {header}
      {d.range.days >= 90 && <Note>Raw events are kept for about 3 months. For anything older, only the daily totals on Overview remain.</Note>}

      <div className={s.toolbar}>
        <div className={clsx("input-shell", s.search)}>
          <IconSearch size={15} />
          <input className={s.searchInput} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Runner, id or event type" aria-label="Search events" />
        </div>
        <div className={s.grow} />
        <span className="t-mono-xs ink-mute">{qParam ? `${n0(events.length)}${d.nextBefore ? "+" : ""} shown` : `${n0(groups[group])} events`}</span>
        <button className={clsx("btn-secondary", s.csv)} onClick={() => void exportCsv()} disabled={exporting || events.length === 0}>
          {exporting ? "Exporting…" : "Export CSV"}
        </button>
      </div>

      <div className={clsx("no-scrollbar", s.chips)}>
        {EVENT_GROUPS.map((g) => (
          <button key={g} className={clsx("chip", s.chip)} data-on={group === g ? "true" : undefined} aria-pressed={group === g} onClick={() => update({ group: g })}>
            {GROUP_LABEL[g]} <span className={clsx("t-mono-xs", s.chipCount)}>{n0(groups[g])}</span>
          </button>
        ))}
      </div>

      <div className={s.table}>
        {!mobile && (
          <div className={s.head} style={{ gridTemplateColumns: cols }}>
            <span className="kicker ink-mute">When</span>
            <span className="kicker ink-mute">Type</span>
            <span className="kicker ink-mute">Runner</span>
            <span className="kicker ink-mute">Summary</span>
          </div>
        )}
        {events.map((e, i) => {
          const bad = isBadEvent(e);
          const isOpen = open === i;
          return (
            <div key={`${e.at}|${i}`} className={s.item}>
              <button className={clsx(s.row, isOpen && s.rowOpen)} style={{ gridTemplateColumns: cols }} aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : i)}>
                <span className="t-mono-xs ink-mute">{fDT(parseWhen(e.at))}</span>
                <span className="t-mono-xs" style={{ color: bad ? "var(--alert)" : "var(--ink-soft)" }}>
                  {e.event}
                </span>
                <span className={clsx("t-body-sm", s.ellipsis)}>{e.runner?.name ?? (e.runnerId ? e.runnerId : "—")}</span>
                <span className={clsx("t-body-sm", s.ellipsis)} style={{ color: bad ? "var(--alert)" : "var(--ink-soft)" }}>
                  {eventSummary(e)}
                </span>
              </button>
              {isOpen && (
                <div className={s.detail}>
                  <div className={s.fields}>
                    <span className="t-mono-xs ink-mute">at (UTC)</span>
                    <span className={clsx("t-mono-xs", s.fieldValue)}>{e.at}</span>
                    {Object.entries(e.fields).map(([k, v]) => (
                      <span key={k} style={{ display: "contents" }}>
                        <span className="t-mono-xs ink-mute">{k}</span>
                        <span className={clsx("t-mono-xs", s.fieldValue)}>{v}</span>
                      </span>
                    ))}
                    {e.durationMs !== null && (
                      <>
                        <span className="t-mono-xs ink-mute">durationMs</span>
                        <span className="t-mono-xs">{e.durationMs}</span>
                      </>
                    )}
                    {e.runner && (
                      <>
                        <span className="t-mono-xs ink-mute">runner</span>
                        <span className={clsx("t-mono-xs", s.fieldValue)}>
                          {e.runner.email} · {e.runnerId}
                        </span>
                      </>
                    )}
                  </div>
                  {e.runnerId && (
                    <Link className={clsx("btn-tertiary", s.open)} to={`/runners/${encodeURIComponent(e.runnerId)}`}>
                      Open {e.runner?.name ?? e.runnerId} →
                    </Link>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {events.length === 0 && (
          <div className={s.empty}>
            <span className="t-serif-md">{groups.all === 0 ? "No events yet." : "Nothing matches."}</span>
            <span className="t-body-sm ink-mute">{groups.all === 0 ? "The first sign-up will show here as auth.sign_up." : "Try another type or clear the search."}</span>
          </div>
        )}
      </div>

      {(pageNo > 0 || d.nextBefore) && (
        <div className={s.pager}>
          <span className="t-mono-xs ink-mute">
            {n0(from)}–{n0(from + events.length - 1)}
            {qParam ? "" : ` of ${n0(groups[group])}`}
          </span>
          <div className={s.pagerBtns}>
            <button
              className={clsx("btn-secondary", s.pagerBtn)}
              disabled={pageNo === 0}
              onClick={() => {
                setOpen(null);
                setCursors((c) => c.slice(0, -1));
              }}
            >
              ← Newer
            </button>
            <button
              className={clsx("btn-secondary", s.pagerBtn)}
              disabled={!d.nextBefore}
              onClick={() => {
                setOpen(null);
                setCursors((c) => [...c, d.nextBefore]);
              }}
            >
              Older →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
